import React, { useEffect, useState } from "react";
import Select from "react-select";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import { useTranslation } from "react-i18next";

import StandardScreen, {
  ScreenContent,
} from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";

export default function GodUpdateUserProfile() {
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [userId, setUserId] = useState(null);
  const [vendorId, setVendorId] = useState(null);

  const [profile, setProfile] = useState({
    sponsor: "",
    sponsor_no: "",
    notes: "",
  });

  const [message, setMessage] = useState("");
  const [loadingLists, setLoadingLists] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.defaults.headers.common.Authorization =
      `Bearer ${localStorage.getItem("token")}`;

    fetchUsersAndVendors();
  }, []);

  useEffect(() => {
    if (!userId || !vendorId) {
      clearProfile();
      return;
    }

    fetchUserProfile(userId, vendorId);
  }, [userId, vendorId]);

  const clearProfile = () => {
    setProfile({
      sponsor: "",
      sponsor_no: "",
      notes: "",
    });
  };

  const fetchUsersAndVendors = async () => {
    setLoadingLists(true);

    try {
      const [
        userResponse,
        vendorResponse,
      ] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_URL}/god/users`
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/god/vendors`
        ),
      ]);

      setUsers(
        Array.isArray(userResponse.data)
          ? userResponse.data
          : []
      );

      setVendors(
        Array.isArray(vendorResponse.data)
          ? vendorResponse.data
          : []
      );
    } catch (error) {
      console.error(
        "❌ Failed to fetch users/vendors",
        error
      );

      setUsers([]);
      setVendors([]);

      setMessage(
        "❌ " +
          (
            error.response?.data?.detail ||
            t(
              "god_update_user_profile.messages.fetch_failed",
              "Failed to fetch users or vendors."
            )
          )
      );
    } finally {
      setLoadingLists(false);
    }
  };

  const fetchUserProfile = async (
    selectedUserId,
    selectedVendorId
  ) => {
    if (
      !selectedUserId ||
      !selectedVendorId
    ) {
      clearProfile();
      return null;
    }

    setLoadingProfile(true);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/god/users/${selectedUserId}/vendors/${selectedVendorId}/profile`
      );

      /*
       * god.py returns:
       *
       * {
       *   exists: true/false,
       *   profile: {...} | null
       * }
       *
       * A missing profile is not an error. It simply means
       * God is creating the user/vendor profile for the first time.
       */
      const loadedProfile =
        response.data?.profile || null;

      setProfile({
        sponsor:
          loadedProfile?.sponsor || "",
        sponsor_no:
          loadedProfile?.sponsor_no || "",
        notes:
          loadedProfile?.notes || "",
      });

      setMessage("");

      return loadedProfile;
    } catch (error) {
      console.error(
        "❌ Failed to fetch user vendor profile",
        error
      );

      clearProfile();

      setMessage(
        "❌ " +
          (
            error.response?.data?.detail ||
            t(
              "god_update_user_profile.messages.fetch_profile_failed",
              "Failed to fetch profile."
            )
          )
      );

      return null;
    } finally {
      setLoadingProfile(false);
    }
  };

  const submit = async () => {
    if (!userId || !vendorId) {
      setMessage(
        "❌ " +
          t(
            "god_update_user_profile.messages.missing_selection",
            "Please select a user and vendor."
          )
      );

      return;
    }

    setSubmitting(true);
    setMessage("");

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/god/update-user-profile`,
        {
          user_id: userId,
          vendor_id: vendorId,
          sponsor: profile.sponsor,
          sponsor_no: profile.sponsor_no,
          notes: profile.notes,
        }
      );

      /*
       * Reload from the backend after the commit.
       * This guarantees that the form displays the actual
       * persisted UserVendorProfile state.
       */
      await fetchUserProfile(
        userId,
        vendorId
      );

      setMessage(
        "✅ " +
          t(
            "god_update_user_profile.messages.success",
            "User profile updated successfully."
          )
      );
    } catch (error) {
      console.error(
        "❌ Failed to update user vendor profile",
        error
      );

      setMessage(
        "❌ " +
          (
            error.response?.data?.detail ||
            t(
              "god_update_user_profile.messages.failure",
              "Failed to update user profile."
            )
          )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const userOptions = users.map(
    (user) => ({
      value: user.user_id,
      label:
        `${user.fullname || "-"} | ` +
        `${user.userno || "-"} | ` +
        `${
          typeof user.role === "string"
            ? user.role
            : user.role?.value || "-"
        }`,
    })
  );

  const vendorOptions = vendors.map(
    (vendor) => ({
      value: vendor.vendor_id,
      label:
        `${vendor.vendor_name || "-"} | ` +
        `${vendor.vendor_id}`,
    })
  );

  const selectedUserOption =
    userOptions.find(
      (option) =>
        option.value === userId
    ) || null;

  const selectedVendorOption =
    vendorOptions.find(
      (option) =>
        option.value === vendorId
    ) || null;

  const formDisabled =
    loadingLists ||
    loadingProfile ||
    submitting;

  return (
    <StandardScreen
      background="default"
      topNav={
        <TopNav
          title={t(
            "god_update_user_profile.title"
          )}
          mode="main"
        />
      }
    >
      <ScreenContent>
        <div
          style={{
            maxWidth: 720,
            width: "100%",
            margin: "0 auto",
          }}
        >
          <Stack gap="4">
            <Card>
              <Stack gap="4">
                <h1
                  style={{
                    margin: 0,
                    fontSize: "1.5rem",
                    fontWeight: 700,
                  }}
                >
                  {t(
                    "god_update_user_profile.title"
                  )}
                </h1>

                <FormField
                  label={t(
                    "god_update_user_profile.fields.select_user.label"
                  )}
                >
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    isLoading={loadingLists}
                    isDisabled={submitting}
                    options={userOptions}
                    value={selectedUserOption}
                    onChange={(option) => {
                      setUserId(
                        option?.value || null
                      );

                      clearProfile();
                      setMessage("");
                    }}
                    placeholder={t(
                      "god_update_user_profile.fields.select_user.placeholder"
                    )}
                  />
                </FormField>

                <FormField
                  label={t(
                    "god_update_user_profile.fields.select_vendor.label"
                  )}
                >
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    isLoading={loadingLists}
                    isDisabled={submitting}
                    options={vendorOptions}
                    value={selectedVendorOption}
                    onChange={(option) => {
                      setVendorId(
                        option?.value || null
                      );

                      clearProfile();
                      setMessage("");
                    }}
                    placeholder={t(
                      "god_update_user_profile.fields.select_vendor.placeholder"
                    )}
                  />
                </FormField>

                {userId && vendorId && (
                  <>
                    <FormField
                      label={t(
                        "god_update_user_profile.fields.sponsor.label"
                      )}
                    >
                      <TextInput
                        value={
                          profile.sponsor
                        }
                        disabled={
                          formDisabled
                        }
                        onChange={(event) =>
                          setProfile(
                            (current) => ({
                              ...current,
                              sponsor:
                                event.target.value,
                            })
                          )
                        }
                      />
                    </FormField>

                    <FormField
                      label={t(
                        "god_update_user_profile.fields.sponsor_no.label"
                      )}
                    >
                      <TextInput
                        value={
                          profile.sponsor_no
                        }
                        disabled={
                          formDisabled
                        }
                        onChange={(event) =>
                          setProfile(
                            (current) => ({
                              ...current,
                              sponsor_no:
                                event.target.value,
                            })
                          )
                        }
                      />
                    </FormField>

                    <FormField
                      label={t(
                        "god_update_user_profile.fields.notes.label"
                      )}
                    >
                      <TextInput
                        value={
                          profile.notes
                        }
                        disabled={
                          formDisabled
                        }
                        onChange={(event) =>
                          setProfile(
                            (current) => ({
                              ...current,
                              notes:
                                event.target.value,
                            })
                          )
                        }
                      />
                    </FormField>

                    <Button
                      fullWidth
                      disabled={
                        formDisabled
                      }
                      onClick={submit}
                    >
                      {submitting
                        ? t(
                            "god_update_user_profile.status.submitting",
                            "Updating..."
                          )
                        : loadingProfile
                        ? t(
                            "god_update_user_profile.status.loading",
                            "Loading..."
                          )
                        : t(
                            "god_update_user_profile.actions.submit"
                          )}
                    </Button>
                  </>
                )}
              </Stack>
            </Card>

            <StatusMessage
              message={message}
            />
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}